import { COLMAPCamerasData, COLMAPPoints3DData } from '@/types/colmap'
import { useAppStore } from '@/store/appStore'
import { resolveUrl, toBlobUrl, blobFetch } from '@/config'

export async function loadCOLMAPData(shopId: string): Promise<{
  cameras: COLMAPCamerasData
  points3d: COLMAPPoints3DData
}> {
  // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
  const dataset = useAppStore.getState().availableDatasets.find(d => d.id === shopId)
  const basePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
  
  try {
    // Load cameras.json
    const camerasResponse = await blobFetch(resolveUrl(`${basePath}/cameras.json`))
    if (!camerasResponse.ok) {
      throw new Error(`Failed to load cameras.json: ${camerasResponse.statusText}`)
    }
    const cameras = await camerasResponse.json() as COLMAPCamerasData
    
    // Load points3d.json
    const pointsResponse = await blobFetch(resolveUrl(`${basePath}/points3d.json`))
    if (!pointsResponse.ok) {
      throw new Error(`Failed to load points3d.json: ${pointsResponse.statusText}`)
    }
    const points3d = await pointsResponse.json() as COLMAPPoints3DData
    
    return { cameras, points3d }
  } catch (error) {
    console.error('Error loading COLMAP data:', error)
    throw error
  }
}

export function getFrameImagePath(shopId: string, frameFile: string, framesPath?: string): string {
  // Use provided framesPath (for sections) or default to shop frames directory
  let path = framesPath
  if (!path) {
    // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
    const dataset = useAppStore.getState().availableDatasets.find(d => d.id === shopId)
    const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
    path = `${datasetBasePath}/frames`
  }
  // Add shopId as query parameter for cache busting
  // This ensures browser doesn't serve cached images from different shops
  return toBlobUrl(`${resolveUrl(path)}/${frameFile}?shop=${shopId}`)
}

/**
 * Get HD image path for zoom-in display.
 * Converts frame_NNNN.jpg → frame_NNNN_hd.jpg
 */
export function getFrameHdImagePath(shopId: string, frameFile: string, framesPath?: string): string {
  const hdFrameFile = frameFile.replace(/\.jpg$/i, '_hd.jpg')
  return getFrameImagePath(shopId, hdFrameFile, framesPath)
}

/**
 * Get 4K image path for deep zoom display.
 * Converts frame_NNNN.jpg → frame_NNNN_4k.jpg
 */
export function getFrameUltraImagePath(shopId: string, frameFile: string, framesPath?: string): string {
  const ultraFrameFile = frameFile.replace(/\.jpg$/i, '_4k.jpg')
  return getFrameImagePath(shopId, ultraFrameFile, framesPath)
}
